/* eslint-disable react/prop-types */
import { ComponentPropsWithoutRef, ElementType, ReactNode, useMemo } from 'react';
import { useDarkMode } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import classnames from 'classnames';
import { ExclamationTriangleAshIcon } from '@glu/icons-react';
import { Tooltip } from '@glu/tooltip-react';
import Error, { IErrorProps } from '../Error/Error';
import LengthIndicator from '../LengthIndicator/LengthIndicator';
import LabelText, { ILabelTextProps } from '../LabelText/LabelText';
import OptionalInlineText from '../OptionalInlineText/OptionalInlineText';
import InputComponent from './InputComponent';
import useStyles from './Input.styles';
import HelpText from '../HelpText/HelpText';

let uniqueId = 0;

type TAsComponent<E extends ElementType> = <Component extends ElementType = E>(
  props: {
    /** Determines if the input should be auto focused on render */
    autoFocus?: boolean;
    /** Determines if dark mode styling should be applied */
    dark?: boolean;
    /** Array of all current errors */
    errors?: IErrorProps['error'][];
    /** Help text to display with input */
    helpText?: ReactNode;
    /** Id applied to the input. If not provided id will be `input-${uniqueId++}` */
    htmlId?: string;
    /** Custom classes applied to input element */
    inputClass?: string;
    labelSpanClass?: ILabelTextProps['className'];
    labelText: ILabelTextProps['label'];
    /** maxLengthIndicatorLimit is used to display value length counter
     *  shows maximal value length limit
     */
    maxLengthIndicatorLimit?: number;
    /** Show an icon inside the input */
    showIconOnError?: boolean;
    /** Determines display of textarea or input element */
    textarea?: boolean;
    /** Applies the error message with a tooltip instead of below the input */
    tooltipErrorMessage?: boolean;
    /** The HTML value of the input */
    value?: string;
    /** Applies warning styling to the element */
    warning?: boolean;
  } & Pick<IErrorProps, 'error'> &
    Pick<ILabelTextProps, 'optional' | 'screenReaderLabel' | 'inline' | 'tooltipContent'> &
    ComponentPropsWithoutRef<Component>
) => JSX.Element;

const Input: TAsComponent<'input'> = (props) => {
  const {
    className,
    dark: propDark,
    error,
    errors,
    helpText,
    htmlId,
    inline,
    labelSpanClass,
    labelText,
    maxLengthIndicatorLimit,
    onChange,
    optional,
    screenReaderLabel,
    showIconOnError,
    textarea,
    tooltipContent,
    tooltipErrorMessage,
    value,
    warning,
    ...rest
  } = props;
  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `input-${uniqueId++}`, [htmlId]);
  const classes = useStyles();
  const errorId = useMemo(() => `error-${id}`, [id]);
  const errorMessage = error || errors?.[0];
  const darkMode = useDarkMode();
  const dark = propDark || darkMode;
  return (
    <label
      className={classnames(classes.root, { [classes.inline]: inline }, className)}
      htmlFor={id}
    >
      <LabelText
        className={labelSpanClass}
        hasError={!!errorMessage}
        inline={inline}
        label={labelText}
        optional={optional}
        screenReaderLabel={screenReaderLabel}
        tooltipContent={tooltipContent}
        warning={warning}
      />
      <div className={classes.wrap}>
        <InputComponent
          id={id}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...rest}
          aria-errormessage={errorMessage ? errorId : undefined}
          aria-invalid={!!errorMessage}
          dark={dark}
          error={errorMessage}
          onChange={onChange}
          textarea={textarea}
          /**
           * We use to have value default to an empty string. However that didn't allow us to use
           * the input as a totally uncontrolled input. See GLU-6812. The below change while ugly
           * handles the use case where onChange is passed from @glu/validation-react, but the value is
           * still undefined. The other alternative is to make the default value passed from @glu/validation-react
           * an empty string, but I'm not sure of the ramifications of that change considering how many
           * places @glu/validation-react is used.
           */
          value={value === undefined && onChange !== undefined ? '' : value}
          warning={warning}
        />
        {(showIconOnError || tooltipErrorMessage) && errorMessage && (
          <div
            className={classnames(classes.tooltipErrorIcon, {
              [classes.tooltipErrorIconDark]: dark,
            })}
          >
            {tooltipErrorMessage ? (
              <Tooltip content={errorMessage} placement="top">
                <ExclamationTriangleAshIcon noIconWrapper />
              </Tooltip>
            ) : (
              <ExclamationTriangleAshIcon noIconWrapper />
            )}
          </div>
        )}

        <div className={classes.helpWrapper}>
          {!tooltipErrorMessage && !showIconOnError && (
            <Error dark={dark} error={errorMessage} htmlId={errorId} warning={warning} />
          )}
          {helpText && !errorMessage && <HelpText>{helpText}</HelpText>}
          <LengthIndicator
            dark={dark}
            hasError={!!errorMessage}
            maxLengthIndicatorLimit={maxLengthIndicatorLimit}
            // eslint-disable-next-line react/destructuring-assignment
            value={value}
          />
        </div>
        {helpText && !!errorMessage && <HelpText>{helpText}</HelpText>}
      </div>
      {optional && inline ? (
        <OptionalInlineText hasError={!!errorMessage} warning={warning} withTextarea={textarea} />
      ) : null}
    </label>
  );
};

const InputWithInputAPI = withInputAPI(Input);

export default InputWithInputAPI;
export const ValidatedInput = InputWithInputAPI;
export const BaseInput = Input;
